import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormHelperText,
  TextField,
  Button,
} from '@mui/material';
import fetchEnvironments from 'store/fetchEnvironments';
import sendNodeCommand from 'store/sendNodeCommand';
import useEnvironmentsAsync from 'store/useEnvironmentsAsync';
import useProjectsAsync from 'store/useProjectsAsync';
import IEnvironment from 'types/IEnvironment';
import IProject from 'types/IProject';

interface StartWorkerDialogProps {
  nodeAgentId: string;
  workdirPackages: Record<string, string>;
  onClose: () => void;
  onSubmit: () => void;
}

export default function StartWorkerDialog({ nodeAgentId, workdirPackages, onClose, onSubmit }: StartWorkerDialogProps) {
  const [packageName, setPackageName] = useState('pytailor');
  const [ncores, setNcores] = useState('1');
  const [sleep, setSleep] = useState('3');
  const [workerName, setWorkerName] = useState('');
  const [workdirName, setWorkdirName] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<IProject[]>([]);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  // Track the Autocomplete's input so a capability the user typed but didn't
  // commit with Enter/blur before clicking Start still gets included.
  const [capabilityInput, setCapabilityInput] = useState('');
  const [pythonVersion, setPythonVersion] = useState('');
  const [batch, setBatch] = useState(true);
  const [v2Execution, setV2Execution] = useState(false);
  const [maxTasksPerProcess, setMaxTasksPerProcess] = useState('1');
  const [selectedEnvironment, setSelectedEnvironment] = useState<IEnvironment | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const existingWorkdirs = Object.keys(workdirPackages);
  const lockedPackage = workdirPackages[workdirName] ?? null;

  const projectsAsync = useProjectsAsync();
  const projects = projectsAsync.status === 'SUCCESS' || projectsAsync.status === 'REFRESH' ? projectsAsync.data : [];

  // Refetch on open so freshly published environments appear without a reload.
  useEffect(() => {
    fetchEnvironments(true);
  }, []);
  const environmentsAsync = useEnvironmentsAsync();
  const environments =
    environmentsAsync.status === 'SUCCESS' || environmentsAsync.status === 'REFRESH' ? environmentsAsync.data : [];

  const packageFieldsDisabled = lockedPackage !== null || selectedEnvironment !== null;

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    const pendingCap = capabilityInput.trim();
    const finalCapabilities =
      pendingCap && !capabilities.includes(pendingCap) ? [...capabilities, pendingCap] : capabilities;
    // 0 is a real value (never recycle) — only fall back to 1 on empty/invalid input.
    const parsedMaxTasks = parseInt(maxTasksPerProcess, 10);
    const finalMaxTasks = Number.isNaN(parsedMaxTasks) || parsedMaxTasks < 0 ? 1 : parsedMaxTasks;
    try {
      await sendNodeCommand(nodeAgentId, 'start_worker', {
        ...(selectedEnvironment
          ? { environment: { name: selectedEnvironment.name, version: selectedEnvironment.version } }
          : {
              package_name: lockedPackage ?? packageName,
              ...(pythonVersion.trim() && { python_version: pythonVersion.trim() }),
            }),
        ncores: parseInt(ncores, 10) || 1,
        sleep: parseFloat(sleep) || 3,
        worker_name: workerName,
        ...(workdirName && { workdir_name: workdirName }),
        ...(selectedProjects.length > 0 && {
          project_id_filter: selectedProjects.map((p) => p.id),
        }),
        ...(finalCapabilities.length > 0 && { capabilities: finalCapabilities }),
        ...(batch && { batch: true }),
        ...(v2Execution && {
          execution_mode: 'v2',
          max_tasks_per_process: finalMaxTasks,
        }),
      });
      onSubmit();
    } catch (err) {
      console.error('Failed to start worker:', err);
      setError('Failed to start worker. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Block dismiss while a request is in flight — the parent unmounts on
  // close, dropping isSubmitting, which would otherwise allow a duplicate
  // submit on reopen.
  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Start Worker</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={environments}
          getOptionLabel={(option) => `${option.name}@${option.version}`}
          value={selectedEnvironment}
          onChange={(_, value) => {
            setSelectedEnvironment(value);
            // Capabilities always mirror the current selection: prefill from the
            // chosen environment, clear when it provides none or is deselected.
            setCapabilities(value?.provides_caps ?? []);
          }}
          disabled={lockedPackage !== null}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Environment (optional)"
              margin="normal"
              helperText={
                lockedPackage !== null
                  ? 'Not available when reusing an existing working directory.'
                  : 'Provision this worker from a published environment instead of a single package.'
              }
            />
          )}
        />
        <TextField
          label="Package Name"
          value={lockedPackage ?? packageName}
          onChange={(e) => setPackageName(e.target.value)}
          fullWidth
          margin="normal"
          disabled={packageFieldsDisabled}
          helperText={
            selectedEnvironment !== null
              ? 'Defined by the selected environment.'
              : lockedPackage !== null
                ? 'Locked to the package installed in the selected working directory.'
                : 'Supports version pinning, e.g. pytailor==0.11.0'
          }
        />
        <TextField
          label="Python Version (optional)"
          value={selectedEnvironment ? selectedEnvironment.python_version : pythonVersion}
          onChange={(e) => setPythonVersion(e.target.value)}
          fullWidth
          margin="normal"
          disabled={packageFieldsDisabled}
          placeholder="e.g. 3.11"
          helperText={
            selectedEnvironment !== null
              ? 'Defined by the selected environment.'
              : lockedPackage !== null
                ? 'Locked when reusing an existing working directory.'
                : "Leave empty to use the node agent's own Python."
          }
        />
        <TextField
          label="Number of Cores"
          type="number"
          value={ncores}
          onChange={(e) => setNcores(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Maximum tasks this worker will run in parallel."
        />
        <TextField
          label="Sleep Interval (seconds)"
          type="number"
          value={sleep}
          onChange={(e) => setSleep(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Seconds the worker waits between check-ins to the server."
        />
        <TextField
          label="Worker Name"
          required
          value={workerName}
          onChange={(e) => setWorkerName(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Identifies this worker. A workflow can be restricted to a worker name so only matching workers pick up its tasks."
        />
        <Autocomplete
          freeSolo
          options={existingWorkdirs}
          value={workdirName}
          onChange={(_, value) => {
            const name = (value as string) || '';
            setWorkdirName(name);
            const pkg = workdirPackages[name];
            // Reusing an existing workdir locks to its package, so the
            // environment branch no longer applies — drop the selection.
            if (pkg) {
              setPackageName(pkg);
              setSelectedEnvironment(null);
            }
          }}
          onInputChange={(_, value) => {
            setWorkdirName(value);
            const pkg = workdirPackages[value];
            if (pkg) {
              setPackageName(pkg);
              setSelectedEnvironment(null);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Working Directory (optional)"
              margin="normal"
              helperText="Leave empty for a fresh environment. Select an existing name to reuse its virtual environment."
            />
          )}
        />
        <Autocomplete
          multiple
          options={projects}
          getOptionLabel={(option) => option.name}
          value={selectedProjects}
          onChange={(_, value) => setSelectedProjects(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project Filters (optional)"
              margin="normal"
              helperText="Only pick up tasks from the selected projects. Leave empty to accept any project."
            />
          )}
        />
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={capabilities}
          onChange={(_, value) => setCapabilities(value as string[])}
          inputValue={capabilityInput}
          onInputChange={(_, value) => setCapabilityInput(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Capabilities (optional)"
              margin="normal"
              helperText="Tags this worker provides. A task that requires a capability will only run on a worker that has it. Type and press Enter to add."
            />
          )}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={batch || v2Execution}
              disabled={v2Execution}
              onChange={(e) => setBatch(e.target.checked)}
            />
          }
          label="Batch task check-ins"
          sx={{ mt: 1, mb: 0 }}
        />
        <FormHelperText sx={{ ml: 4, mt: 0 }}>
          {v2Execution
            ? 'Always on for v2 execution.'
            : 'Bundles task updates into the periodic worker check-in instead of sending them individually. Recommended for large/high load workers (requires pytailor>=0.13.0).'}
        </FormHelperText>
        <FormControlLabel
          control={<Checkbox checked={v2Execution} onChange={(e) => setV2Execution(e.target.checked)} />}
          label="v2 execution (process recycling)"
          sx={{ mt: 1, mb: 0 }}
        />
        <FormHelperText sx={{ ml: 4, mt: 0 }}>
          Recycles task subprocesses (requires pytailor&gt;=0.21.0).
        </FormHelperText>
        {v2Execution && (
          <TextField
            label="Max tasks per process"
            type="number"
            value={maxTasksPerProcess}
            onChange={(e) => setMaxTasksPerProcess(e.target.value)}
            fullWidth
            margin="normal"
            slotProps={{ htmlInput: { min: 0 } }}
            helperText="Tasks a subprocess runs before recycling. 1 = a fresh process per task; higher amortizes startup on light workloads; 0 = never recycle."
          />
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!workerName.trim() || isSubmitting}
        >
          {isSubmitting ? 'Starting\u2026' : 'Start'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
